/**
 * IcgDomainExplainer — explainer for ICG-Pa, the DOMAIN Immunity Closure
 * Guarantee (governance name: DOMAIN Safety Envelope).
 *
 * ICG-Pa pairs the SEPARATE Neutralization Theorem (primal) with the
 * PARTITION Structural Duality Principle (dual). PARTITION is the unique
 * SDP whose duality action is `fix` rather than `swap`, so primal and dual
 * coincide: the envelope is a self-dual fixed point that witnesses domain
 * separation at every granularity at once.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import {
  AuditSurfaceDelta,
  CalloutBox,
  CounterModelGrid,
  ExplainerCard,
  SafetyEnvelopeDiagram,
  type ExplainerCrossLink,
  type ExplainerIdentity,
  type ExplainerTierContent,
} from "../shared/index.js";
import { COMPOSITIONALITY_PRINCIPLE_COLOR, THEOREM_COLOR } from "../../design-system/tokens.js";

const PRIMAL_ACCENT = THEOREM_COLOR.SEPARATE;
const DUAL_ACCENT = COMPOSITIONALITY_PRINCIPLE_COLOR.PARTITION.color;

const COUNTER_MODELS = [
  {
    title: "Primal only — SEPARATE, no PARTITION",
    body: "A domain boundary holds at the granularity the audit examined, but a finer decomposition is unwitnessed: a sub-component boundary the top-level check never inspected can leak knowledge across domains. The audit confirms one scale and trusts the rest.",
  },
  {
    title: "Dual only — PARTITION, no SEPARATE",
    body: "The self-dual action would certify separation at every scale, but with no SEPARATE neutralization there is no boundary to witness at any scale — the fixed point ranges over an empty guarantee.",
  },
] as const;

const IDENTITY: ExplainerIdentity = {
  id: "explainer-icg-pa",
  kind: "SSM · Immunity Closure Guarantee (S1⊗S2)",
  title: "ICG-Pa",
  subtitle: "DOMAIN Safety Envelope · SEPARATE ⊗ PARTITION",
  accent: PRIMAL_ACCENT,
  accentAlt: DUAL_ACCENT,
};

const CROSS_LINKS: readonly ExplainerCrossLink[] = [
  { label: "SEPARATE (primal NT)", href: "#explainer-separate", accent: PRIMAL_ACCENT },
  { label: "PARTITION (dual SDP)", href: "#explainer-partition", accent: DUAL_ACCENT },
  { label: "Double category", href: "#explainer-double-category" },
];

const ENVELOPE = (
  <SafetyEnvelopeDiagram
    icg="ICG-Pa"
    primalLabel="SEPARATE"
    primalKind="Neutralization Theorem · S"
    primalAccent={PRIMAL_ACCENT}
    dualLabel="PARTITION"
    dualKind="Structural Duality Principle · Pa"
    dualAccent={DUAL_ACCENT}
    adjunction="parameterized self-dual (fix)"
    unitRole="boundary splitting"
    counitRole="recombination"
  />
);

const TIERS: ExplainerTierContent = {
  tldr: (
    <p>
      <strong>ICG-Pa</strong> is the DOMAIN Safety Envelope. It seals domain neutralization
      (SEPARATE) and its compositional dual (PARTITION) into one two-sided certificate. Uniquely
      among the seven envelopes, primal and dual coincide — the envelope is a self-dual fixed point.
    </p>
  ),
  intuition: (
    <>
      <p>
        SEPARATE makes a cross-domain epistemic lift unconstructable at a fixed granularity.
        PARTITION certifies that the boundary survives every refinement — at every scale of
        decomposition, the same separation holds.
      </p>
      <p>
        For the other six envelopes the dual side swaps the primal into something else (a fibration
        becomes an opfibration, an algebra becomes a coalgebra). For ICG-Pa it does not: the duality
        action is the identity. PARTITION is the only SDP whose action is <code>fix</code> rather
        than <code>swap</code>, so the dual side already <em>is</em> the primal side, witnessed at
        every scale.
      </p>
      <p>
        Even though primal and dual coincide, ICG-Pa is <strong>strictly stronger</strong> than the
        bare SEPARATE theorem: the fixed point carries an all-granularities quantifier the theorem
        alone lacks.
      </p>
    </>
  ),
  formal: (
    <>
      <p>
        ICG-Pa is the parameterized self-dual double functor. Among the seven SDPs the duality
        action takes one of two values — <code>swap</code> or <code>fix</code> — over a cell type
        with exactly two constructors; PARTITION is the unique action equal to <code>fix</code>, the
        identity. That 6-swap-and-1-fix split is a theorem of the cell type, not a design choice.
        Its structural witnesses are:
      </p>
      <ul>
        <li>
          the <strong>unit η</strong> — boundary splitting: a domain refined into sub-domains, each
          inheriting the separation property of the whole;
        </li>
        <li>
          the <strong>counit ε</strong> — recombination: the sub-domains recovered as the original
          boundary, with the separation property preserved.
        </li>
      </ul>
      <p>
        The triangle identities make splitting and recombination mutually inverse, so the boundary
        holds at every scale simultaneously — there is no granularity at which it silently
        disappears.
      </p>
      <AuditSurfaceDelta
        primalCheck="SEPARATE witness"
        dualCheck="PARTITION witness"
        envelopeLabel="ICG-Pa envelope"
        primalAccent={PRIMAL_ACCENT}
        dualAccent={DUAL_ACCENT}
      />
      <p style={{ marginTop: "0.5rem" }}>
        Categorical form: ICG-Pa is the <em>Adjunction Certificate</em> in its degenerate but
        non-trivial shape — the unit and counit of a self-dual fixed point witness separation across
        the whole granularity tower, not at a single scale.
      </p>
    </>
  ),
  relevance: (
    <>
      <p>
        <strong>Why "strictly stronger".</strong> Even though primal and dual coincide, each side
        viewed alone admits a counter-model the envelope rules out:
      </p>
      <CounterModelGrid counterModels={COUNTER_MODELS} ariaLabel="ICG-Pa counter-models" />
      <p style={{ marginTop: "0.75rem" }}>
        ICG-Pa makes the forbidden generator <strong>G2 (epistemic lift)</strong> structurally
        absent at every granularity: lifting knowledge across a domain boundary without an explicit
        bridge is unconstructable, at every scale of decomposition. The envelope enforces security
        axiom A2, Witness-Before-Effect.
      </p>
      <p>
        <strong>Worked adversary.</strong> A cross-domain prompt injection: content from a low-trust
        domain (retrieved documents, tool output, untrusted user text) is lifted into a high-trust
        domain (the agent's instruction context) by exploiting a granularity at which the domain
        boundary was only checked coarsely — a sub-component boundary the top-level separation audit
        never inspected. Under ICG-Pa the self-dual fixed point witnesses separation at every scale,
        so the unchecked granularity does not exist.
      </p>
      <p>
        <strong>Theorem-collision check.</strong> Bypassing ICG-Pa means bypassing SEPARATE and
        PARTITION at once, or escaping the double category — both leave the kernel category and
        vacate the guarantee rather than violate it.
      </p>
      <CalloutBox eyebrow="Safety envelope: ICG-Pa" accent={DUAL_ACCENT}>
        SEPARATE (NT) ⊗ PARTITION (SDP) = ICG-Pa. The audit surface collapses from an NT witness
        plus an SDP witness to one envelope — a self-dual fixed point carrying its unit and counit.
      </CalloutBox>
    </>
  ),
  references: [
    {
      label: "Theorem Canon — Immunity Closure Guarantees (§7a)",
      path: "docs/reference/Theorem-Canon.md",
    },
    {
      label: "RD-97 — Immunity Closure Guarantees",
      path: "research/settled/RD-97-BI-GUARANTEES-TWO-SIDED-SAFETY.md",
      note: "Canonical source for the seven-envelope algebra and the naming triad.",
    },
    {
      label: "Safety Envelope foundations",
      path: "theory/structural/Immunity-Closure-Guarantees/00-Safety-Envelope-Foundations.md",
    },
    {
      label: "ICG-Pa theory document",
      path: "theory/structural/Immunity-Closure-Guarantees/07-ICG-Pa-Separate-And-Partition.md",
    },
    {
      label: "Immunity Closure Guarantee (Agda)",
      path: "agda/Research/BiGuarantee/BiSeparation.agda",
    },
  ],
};

export function IcgDomainExplainer(): React.JSX.Element {
  return (
    <ExplainerCard identity={IDENTITY} tiers={TIERS} diagram={ENVELOPE} crossLinks={CROSS_LINKS} />
  );
}
